// composables/useBanners.ts
import type { NuxtApp } from "#app";

/**
 * useBanners composable
 * Fetches and caches banner data from the `/banners` API.
 *
 * @param type - Banner type (e.g., 'homePage', 'productPage', etc.)
 * @returns { data, pending, error, refresh }
 *
 * Notes:
 * - Uses useAsyncData for automatic caching and revalidation.
 * - Cache key includes banner type to prevent data collisions.
 * - Automatically sorts results using the 'order' field.
 * - Supports SSR hydration and client-side cache reuse.
 */
export function useBanners(type: string, options: unknown = {}, server: boolean = true) {
    const key = `banners-${type}`;
    const defaultOptions = {
        params: {
            filter: { type },
            sort: "order",
        },
    };
    const fetchOptions = useMerge(defaultOptions, options);

    // Async fetch with caching & reactivity
    const { data, status, pending, error, refresh } = useAsyncData<APIPaginate<APIBanner>>(
        key,
        () => smartFetch<APIPaginate<APIBanner>>("/banners", fetchOptions),
        {
            server,
            default: () => ({
                data: [],
            }),
            getCachedData: (key: string, nuxtApp: NuxtApp) => {
                return nuxtApp.payload.data[key] || nuxtApp.static.data[key];
            },
        }
    );

    return { data, pending, status, error, refresh };
}
